import React, { createContext, useMemo, useState, useContext } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { PaletteMode } from "@mui/material";

const ColorModeContext = createContext<{
  toggleColorMode: () => void;
  mode: PaletteMode;
}>({
  toggleColorMode: () => {},
  mode: "light",
});

export const useColorMode = () => useContext(ColorModeContext);

const primaryColor = "#004f9f";       // Hauptfarbe qde-CI
const secondaryColor = "#122941";     // Nebenfarbe qde-CI
const acceptColor = "#94c21f";        // Akzeptfarbe qde-CI
const lightGray = "#dadada";          // Hellgrau qde-CI
const veryLightGrey = "#f0f0f0"       // NEU
const mediumGray = "#878787";         // Mittelgrau qde-CI
const darkGray = "#575757";           // Dunkelgrau qde-CI
const veryDarkGrey = "#18171D";       // NEU

export const ColorModeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [mode, setMode] = useState<PaletteMode>(prefersDarkMode ? "dark" : "light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prevMode) => (prevMode === "light" ? "dark" : "light")),
      mode,
    }),
    [mode]
  );

  const theme = useMemo(
      () =>
        createTheme({
          palette: {
            mode,
            primary: {
              main: primaryColor,
              contrastText: "#fff",
            },
            secondary: {
              main: mode === "light" ? secondaryColor : acceptColor,
              contrastText: "#fff",
            },
            success: {
              main: acceptColor,
              contrastText: "#000000",
            },
            background: {
              default: mode === "light" ? veryLightGrey : veryDarkGrey,
              paper: mode === "light" ? "#fff" : veryDarkGrey,
            },
            text: {
              primary: mode === "light" ? veryDarkGrey : lightGray,
              secondary: mode === "light" ? darkGray : lightGray,
            },
            divider: mediumGray,
          },
          typography: {
            fontFamily: '"Myriad Pro", "Helvetica", "Arial", sans-serif',
          },
          components: {
            MuiButton: {
              styleOverrides: {
                root: {
                  borderRadius: 6,
                  textTransform: "none",
                  padding: "6px 16px",
                },
                outlinedPrimary: {
                  color: mode === "light" ? primaryColor : "#fff",
                },
              },
            },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: mode === "light" ? darkGray : lightGray,
                "&.Mui-focused": {
                  color: mode === "light" ? primaryColor : lightGray,
                },
                "&.Mui-disabled": {
                  color: mediumGray,
                },
              },
            },
          },
          },
        }),
      [mode]
    );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
